import { memo, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaProjectDiagram, FaTable } from 'react-icons/fa';
import type { ContentBlock, ToggleGroup } from '../types';

const ICONS: Record<'table' | 'diagram', ReactNode> = {
    table: <FaTable className="text-base" aria-hidden="true" />,
    diagram: <FaProjectDiagram className="text-base" aria-hidden="true" />,
};

// Same segmented-button styling as ThematicStudiesSearchBar, so the two
// "switch between two views" affordances in the app look and behave alike.
const MODE_BUTTON_BASE_CLASSNAME =
    'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition-colors';
const MODE_BUTTON_ACTIVE_CLASSNAME = 'bg-darkBlue text-white dark:bg-lightBlue dark:text-black';
const MODE_BUTTON_INACTIVE_CLASSNAME =
    'text-default-500 hover:bg-default-100 hover:text-darkBlue dark:text-default-400 dark:hover:bg-default-800 dark:hover:text-lightBlue';

interface ContentToggleProps {
    toggle: ToggleGroup;
    renderBlocks: (blocks: ContentBlock[]) => ReactNode;
}

function ContentToggle({ toggle, renderBlocks }: ContentToggleProps) {
    const initialViewId = toggle.defaultViewId ?? toggle.views[0]?.id;
    const [activeViewId, setActiveViewId] = useState(initialViewId);
    const activeView = toggle.views.find((view) => view.id === activeViewId) ?? toggle.views[0];

    if (!activeView) {
        return null;
    }

    return (
        <div className="my-6">
            <div
                className="mb-4 inline-flex items-center gap-1 rounded-lg border border-default-200 bg-white/80 p-1 shadow-sm dark:border-default-700 dark:bg-default-900/70"
                role="group"
                aria-label="Content view"
            >
                {toggle.views.map((view) => (
                    <button
                        key={view.id}
                        type="button"
                        onClick={() => setActiveViewId(view.id)}
                        aria-pressed={activeViewId === view.id}
                        title={view.label}
                        className={`${MODE_BUTTON_BASE_CLASSNAME} ${
                            activeViewId === view.id ? MODE_BUTTON_ACTIVE_CLASSNAME : MODE_BUTTON_INACTIVE_CLASSNAME
                        }`}
                    >
                        {view.icon && ICONS[view.icon]}
                        {view.label}
                    </button>
                ))}
            </div>
            {/*
             * Unlike ThematicStudiesSearchBar's two panels (fixed-height search inputs),
             * these views can be very different heights (a table vs. a large diagram), so we
             * fade/shift rather than clip to a fixed height with overflow hidden.
             */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={activeView.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                >
                    {renderBlocks(activeView.blocks)}
                </motion.div>
            </AnimatePresence>
        </div>
    );
}

export default memo(ContentToggle);
